import * as unitsReducer from './units.reducer';
import * as unitGroupsReducer from './unit-groups.reducer';
import * as positionsReducer from './positions.reducer';
import * as unitAllDataReducer from './unit-all-data.reducer';
import * as controlledInputsReducer from './controlled-inputs.reducer';
import { Action, ActionReducerMap, createFeatureSelector, createSelector } from '@ngrx/store';
import * as fromRoot from '../../reducers';
import {GroupedUnits} from '../../../units/models/grouped-units';
import { UnitStats } from '../../../units/models/unit-stats';
import { PositionStatus } from '../../../units/models/position-status';
import { UnitAllData } from '../../../units/models/unit-all-data';
import { DataSource } from '../../../shared/models/data-source';
import { UnitGroups } from '../../../units/models/unit-groups';

export interface UnitState {
  units: unitsReducer.State;
  unitGroups: unitGroupsReducer.State;
  positions: positionsReducer.State;
  unitAllData: unitAllDataReducer.State;
  controlledInputs: controlledInputsReducer.State;
}

export interface State extends fromRoot.State {
  unit: UnitState;
}

export const reducers: ActionReducerMap<UnitState, Action> = {
  units: unitsReducer.reducer,
  unitGroups: unitGroupsReducer.reducer,
  positions: positionsReducer.reducer,
  unitAllData: unitAllDataReducer.reducer,
  controlledInputs: controlledInputsReducer.reducer,
};

export const selectUnitState = createFeatureSelector<State, UnitState>('unit');

export const selectUnitsState = createSelector(selectUnitState, (state: UnitState) => state.units);
export const getUnits = createSelector(selectUnitsState, unitsReducer.getUnits);
export const getUnitsByIds = createSelector(selectUnitsState, unitsReducer.getUnitsByIds);
export const areUnitsLoading = createSelector(selectUnitsState, unitsReducer.areUnitsLoading);

export const selectUnitGroupsState = createSelector(selectUnitState, (state: UnitState) => state.unitGroups);
export const getUnitGroups = createSelector(selectUnitGroupsState, unitGroupsReducer.getUnitGroups);

export const selectPositionsState = createSelector(selectUnitState, (state: UnitState) => state.positions);
export const getPositions = createSelector(selectPositionsState, positionsReducer.getPositions);
export const getPositionCache = createSelector(selectPositionsState, positionsReducer.getPositionCache);
export const getPositionCacheById = createSelector(getPositionCache, (positionCache, unitId) => positionCache[unitId]);
export const getPositionsLastUpdate = createSelector(selectPositionsState, positionsReducer.getPositionsLastUpdate);
export const arePositionsLoading = createSelector(selectPositionsState, positionsReducer.arePositionsLoading);

export const selectUnitAllDataState = createSelector(selectUnitState, (state: UnitState) => state.unitAllData);
export const getUnitAllData = createSelector(selectUnitAllDataState, unitAllDataReducer.getUnitAllData);
export const getUnitDataById$ = createSelector(getUnitAllData, (units, unitId: number) => units?.find(unit => unit.unit.unitId === unitId));
export const getSelectedUnitData = createSelector(selectUnitAllDataState, unitAllDataReducer.getSelectedUnitData);
export const getUnitData = createSelector(selectUnitAllDataState, unitAllDataReducer.getUnitData);
export const getZoomUnit = createSelector(selectUnitAllDataState, unitAllDataReducer.getZoomUnit);
export const getUnitStats = createSelector(selectUnitAllDataState, unitAllDataReducer.getUnitStats);


export const selectControlledInputsState = createSelector(selectUnitState, (state: UnitState) => state.controlledInputs);
export const getControlledInputs = createSelector(selectControlledInputsState, controlledInputsReducer.getControlledInputs);
export const getAllControlledInputs = createSelector(selectControlledInputsState, controlledInputsReducer.getAllControlledInputs);

export const getGroupedUnits = createSelector(getUnitGroups, getUnitAllData, (groups, units) => {
  if (groups && units && groups.length > 0 && units.length > 0) {
    const groupedUnits: Map<number, GroupedUnits> = new Map<number, GroupedUnits>();

    const sortedGroups = groups.sort((a: any, b: any) => {
      if (a.type === 2 && b.type !== 2) return 1;
      if (b.type === 2 && a.type !== 2) return -1;
      return 0;
    });

    sortedGroups.forEach(g => {
      const filteredUnits = units.filter(u => g.unitIds.includes(u.unit.unitId));

      const stats: UnitStats = {
        business: 0,
        private: 0,
        moving: 0,
        parked: 0,
        gpsOn: 0,
        gpsOff: 0,
        gsmOn: 0,
        gsmOff: 0,
      };

      filteredUnits.forEach(un => {
        if (un.position?.status === PositionStatus.MOVING) {
          stats.moving += 1;
        } else {
          stats.parked += 1;
        }
      });

      groupedUnits.set(g.unitGroupId, {
        group: g,
        units: filteredUnits,
        stats: stats,
      });
    });

    return groupedUnits;
  }
  return null;
});
export const getGroupedUnitsDataSource = createSelector(getUnitGroups, getUnitAllData,  (groups, units) => {
  if (groups && units && groups.length > 0 && units.length > 0) {
    const sortedGroups = groups.sort((a: any, b: any) => {
      return ((a['name'] === b['name']) ? 0 : ((a['name'] > b['name']) ? 1 : -1));
    });
    const gU: any[] = [];
    sortedGroups.forEach((group: UnitGroups) => {
      const g = {
        group: group,
        data: units.filter((unit: UnitAllData) => group.unitIds.indexOf(unit.unit.unitId) > -1)
      };
      gU.push(g);
    });

    const groupedData: DataSource = {
      groups: gU,
      data: units
    };
    return groupedData;
  }
  return null;
});

export const areUnitsDataLoading = createSelector(areUnitsLoading, arePositionsLoading,  (unitsLoading, positionLoading) => {
  return unitsLoading || positionLoading;
});
